import { useEffect, useRef, useState } from 'react';
import type { ContentfulArticleSearchResponse, GetArticlePageParams } from 'autogen/swagger/all';
import { useGetArticlePageQuery } from 'autogen/swagger/all/hooks';
import useUpdateSearchParams from './useUpdateSearchParams';

export function useUpdateArticlePage(initialData?: ContentfulArticleSearchResponse) {
    const [data, setData] = useState(initialData);
    const [shouldAppendArticles, setShouldAppendArticles] = useState(false); // for loading more articles
    const updateSearchParams = useUpdateSearchParams();

    const [params, setParams] = useState<GetArticlePageParams>({
        page: 1,
        pagesize: 24
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const page = searchParams.get('page') || 1;
        const pageSize = searchParams.get('pageSize') || 24;
        const section = searchParams.getAll('section');
        const topic = searchParams.getAll('topic');

        setParams({
            page: Number(page),
            pagesize: Number(pageSize),
            section: section,
            topic: topic
        });
    }, []);

    const paramsRef = useRef(params);
    paramsRef.current = params;

    const { refetch: getArticlePage } = useGetArticlePageQuery(params, {
        initialData,
        enabled: false,
        onSuccess: (result) => {
            const articleGroups = data?.articleGroups;

            if (articleGroups && shouldAppendArticles) {
                result.articleGroups[0].articles.hits = [
                    ...data.articleGroups[0].articles.hits,
                    ...result.articleGroups[0].articles.hits
                ];
            }

            updateSearchParams(params);
            setData(result);
        }
    });

    async function updateArticlePage(newParams: Partial<GetArticlePageParams>, appendArticles = false) {
        setShouldAppendArticles(appendArticles);
        const mergedParams = { ...paramsRef.current, ...newParams };
        setParams(mergedParams);

        await new Promise<void>((resolve) => {
            setParams(mergedParams);
            resolve();
        });

        await getArticlePage();
    }

    return {
        data,
        updateArticlePage
    };
}
